import logger from "../../../../utils/logger";
import { getUser } from "../../../../services/localService";

// Get current user's organization ID from localStorage
export const getCurrentUserOrganizationId = () => {
  try {
    const currentUserData = getUser(); // Use the existing getUser function
    logger.log("Current user data from localStorage:", currentUserData);

    // Check if we have the nested structure: { user: { organizationId: ... } }
    if (currentUserData && currentUserData.user && currentUserData.user.organizationId) {
      logger.log("Found organization ID in nested structure:", currentUserData.user.organizationId);
      return currentUserData.user.organizationId;
    }

    // Check if the organizationId is directly on the user object
    if (currentUserData && currentUserData.organizationId) {
      logger.log("Found organization ID in direct structure:", currentUserData.organizationId);
      return currentUserData.organizationId;
    }

    // Check nested organization.id structure
    if (currentUserData?.organization?.id) {
      logger.log("Found organization ID in organization.id:", currentUserData.organization.id);
      return currentUserData.organization.id;
    }

    // Check localStorage directly as fallback
    const directUserData = localStorage.getItem("currentUser");
    if (directUserData) {
      try {
        const parsed = JSON.parse(directUserData);
        logger.log("Direct localStorage data:", parsed);

        if (parsed && parsed.user && parsed.user.organizationId) {
          logger.log("Found organization ID in direct localStorage nested structure:", parsed.user.organizationId);
          return parsed.user.organizationId;
        }

        if (parsed && parsed.organizationId) {
          logger.log("Found organization ID in direct localStorage:", parsed.organizationId);
          return parsed.organizationId;
        }

        if (parsed?.organization?.id) {
          logger.log("Found organization ID in direct localStorage organization.id:", parsed.organization.id);
          return parsed.organization.id;
        }
      } catch (parseError) {
        logger.error("Error parsing direct localStorage data:", parseError);
      }
    }

    logger.warn("No organization ID found in any structure. User data:", currentUserData);
    return null;
  } catch (error) {
    logger.error("Error getting current user organization ID:", error);
    return null;
  }
};

export const getCurrentUserOrganizationType = () => {
  try {
    const currentUserData = getUser();
    logger.log("Getting organization type from user data:", currentUserData);

    // Check direct structure: { organization: { type: { id: ... } } }
    if (currentUserData?.organization?.type?.id) {
      logger.log("Found organization type ID in direct structure (type.id):", currentUserData.organization.type.id);
      return currentUserData.organization.type.id;
    }

    // Check direct structure: { organization: { typeId: ... } }
    if (currentUserData?.organization?.typeId) {
      logger.log("Found organization type ID in direct structure (typeId):", currentUserData.organization.typeId);
      return currentUserData.organization.typeId;
    }

    // Check nested structure: { user: { organization: { type: { id: ... } } } }
    if (currentUserData?.user?.organization?.type?.id) {
      logger.log(
        "Found organization type ID in nested structure (type.id):",
        currentUserData.user.organization.type.id
      );
      return currentUserData.user.organization.type.id;
    }

    // Check nested structure: { user: { organization: { typeId: ... } } }
    if (currentUserData?.user?.organization?.typeId) {
      logger.log("Found organization type ID in nested structure (typeId):", currentUserData.user.organization.typeId);
      return currentUserData.user.organization.typeId;
    }

    // Check nested structure: { user: { organization: { organizationType: { id: ... } } } } (fallback)
    if (currentUserData?.user?.organization?.organizationType?.id) {
      logger.log(
        "Found organization type ID in nested structure (organizationType.id):",
        currentUserData.user.organization.organizationType.id
      );
      return currentUserData.user.organization.organizationType.id;
    }

    // Check direct structure: { organizationType: { id: ... } }
    if (currentUserData?.organizationType?.id) {
      logger.log("Found organization type ID in direct structure:", currentUserData.organizationType.id);
      return currentUserData.organizationType.id;
    }

    // Check localStorage directly as fallback
    const directUserData = localStorage.getItem("currentUser");
    if (directUserData) {
      try {
        const parsed = JSON.parse(directUserData);

        if (parsed?.user?.organization?.type?.id) {
          logger.log("Found organization type ID in direct localStorage (type.id):", parsed.user.organization.type.id);
          return parsed.user.organization.type.id;
        }

        if (parsed?.user?.organization?.typeId) {
          logger.log("Found organization type ID in direct localStorage (typeId):", parsed.user.organization.typeId);
          return parsed.user.organization.typeId;
        }

        if (parsed?.user?.organization?.organizationType?.id) {
          logger.log(
            "Found organization type ID in direct localStorage (organizationType.id):",
            parsed.user.organization.organizationType.id
          );
          return parsed.user.organization.organizationType.id;
        }

        if (parsed?.organizationType?.id) {
          logger.log("Found organization type ID in direct localStorage:", parsed.organizationType.id);
          return parsed.organizationType.id;
        }
      } catch (parseError) {
        logger.error("Error parsing direct localStorage data:", parseError);
      }
    }

    logger.warn("No organization type ID found. User data:", currentUserData);
    return null;
  } catch (error) {
    logger.error("Error getting current user organization type:", error);
    return null;
  }
};

// Transform API organizations to company format for display
export const transformOrganizationsToCompanies = organizations => {
  if (!organizations || organizations.length === 0) return [];

  return organizations.map(organization => ({
    id: organization.id,
    name: organization.name,
    industry: organization.type?.name || "Unknown", // Use organization type name
    employeeCount: organization.config?.maxUsers || 0, // Use maxUsers from config
    lastActive: organization.modifiedAt ? new Date(organization.modifiedAt).toLocaleDateString() : "Unknown",
    employees: [], // This would need to come from a separate API call for organization users
    status: organization.status,
    contactEmail: organization.contactEmail,
    alternateEmail: organization.alternateEmail,
    contactMsisdn: organization.contactMsisdn,
    code: organization.code,
    address: organization.address,
    city: organization.city,
    region: organization.region,
    logoUrl: organization.logoUrl,
    domain: organization.domain,
    webUrl: organization.webUrl,
    spaceType: organization.spaceType,
    countryId: organization.countryId,
    typeId: organization.typeId,
    country: organization.country,
    type: organization.type,
    config: organization.config,
    createdAt: organization.createdAt,
    modifiedAt: organization.modifiedAt,
    statusLastModifiedAt: organization.statusLastModifiedAt,
    originalData: organization,
  }));
};

// Get companies from API data (all filtering is handled server-side)
export const getFilteredCompanies = organizations => {
  let companies = transformOrganizationsToCompanies(organizations);
  // Filter out HOST_INDIVIDUAL_CUSTOMER organizations (system-generated, not user-created)
  companies = companies.filter(company => company.type?.code !== "HOST_INDIVIDUAL_CUSTOMER");
  return companies;
};

// Get unique organization types for industry filter
export const getAvailableIndustries = organizations => {
  const types = new Set();
  organizations.forEach(org => {
    if (org.type?.name) {
      types.add(org.type.name);
    }
  });
  return Array.from(types);
};

// Get unique countries for country filter
export const getAvailableCountries = organizations => {
  const countries = new Set();
  organizations.forEach(org => {
    if (org.country?.name) {
      countries.add(org.country.name);
    }
  });
  return Array.from(countries);
};

// Group organizations by company name (kept for potential future use)
export const groupOrganizationsByCompany = organizations => {
  const grouped = {};
  organizations.forEach(org => {
    const companyName = org.name || "Unknown Company";
    if (!grouped[companyName]) grouped[companyName] = [];
    grouped[companyName].push(org);
  });
  return grouped;
};

// Get employee organization type IDs (corporate customers and host organization staff)
export const getEmployeeOrganizationTypeIds = organizationTypes => {
  // Filter organization types based on codes to find employee/business types
  const employeeOrgTypes = organizationTypes
    .filter(
      type =>
        type.code === "CORPORATE_CUSTOMER" ||
        type.code === "HOST_PROPERTY_DISTRIBUTION" ||
        type.code === "HOST_PROPERTY_MANAGEMENT"
    )
    .map(type => type.id);

  logger.log("Employee organization type IDs:", employeeOrgTypes);
  return employeeOrgTypes;
};

// Get individual customer organization type IDs
export const getIndividualCustomerOrganizationTypeIds = organizationTypes => {
  // Filter organization types for individual customer types
  const individualOrgTypes = organizationTypes
    .filter(
      type =>
        type.code === "INDIVIDUAL_CUSTOMER" ||
        type.code === "SUBSCRIBER_INDIVIDUAL" ||
        type.name?.toLowerCase().includes("individual") ||
        type.name?.toLowerCase().includes("subscriber")
    )
    .map(type => type.id);

  logger.log("Individual customer organization type IDs:", individualOrgTypes);
  return individualOrgTypes;
};

// Refresh functions
export const createRefreshFunction = (
  fetchOrganizations,
  currentStatus,
  pagination,
  industryTypeId,
  countryId,
  spaceTypeFilter,
  orderByFilter
) => {
  return () => {
    fetchOrganizations(currentStatus, 1, pagination.limit, industryTypeId, countryId, spaceTypeFilter, orderByFilter);
  };
};
